import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  CallOutlineSegment,
  TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Seconds } from "@repo/app-types/units/duration";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconMessage, IconPhone } from "@tabler/icons-react";
import { DateTime, Duration } from "luxon";
import { useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  NavigationRail,
  NavigationRailHeaderItem,
  NavigationRailItem,
  useNavigationRailItems,
  type NavigationRailMeasuredItem,
} from "../../components/navigationRail/navigationRail.js";
import {
  computeNavigationRailGapThumb,
  computeNavigationRailThumb,
  type NavigationRailThumbPosition,
} from "../../components/navigationRail/navigationRailThumb.js";
import { renderCallLength } from "./durationFormatting.js";
import {
  buildTranscriptNavigationItems,
  checkCallSegmentContainsPosition,
  describeTranscriptNavigationItems,
  type CallPosition,
  type TextConversationNavigationItem,
  type TranscriptPosition,
} from "./transcriptNavigationHelpers.js";

export interface TranscriptNavigatorProps {
  recordings: readonly AccountGradingRecording[];
  textThread?: CustomerInteractionTextThread;
  callOutline: TeammateAccountCallOutline | undefined;
  isOutlineLoading?: boolean;
  isOutlineError?: boolean;
  /** The recording selected in the transcript audio player. */
  expandedRecordingId: RecordingId | undefined;
  /** Shows every call's key moments without disclosure controls. */
  expandAll?: boolean;
  /** The key moment under the audio playhead. */
  audioPosition?: CallPosition;
  /** The call fragment or text on the transcript's reading line. */
  readingPosition?: TranscriptPosition;
  onCallRequest: (recordingId: RecordingId) => void;
  onSegmentRequest: (position: CallPosition) => void;
  onTextConversationRequest: (textConversationId: CallTaskEventId) => void;
}

export interface CallNavigationRowProps {
  callNumber: number;
  recordedAt: string;
  duration: Seconds;
  title: string;
  keyMoment?: CallOutlineSegment;
  /** Keeps the key-moment line's space when no moment matches the current position. */
  reserveKeyMomentSpace?: boolean;
  isExpanded?: boolean;
  isStuck?: boolean;
  /** Connects the call icon to its expanded key moments. */
  showKeyMomentRail?: boolean;
  chaptersId?: string;
  onPress?: () => void;
  className?: string;
}

export const CallNavigationRow: FC<CallNavigationRowProps> = (props) => {
  const {
    callNumber,
    recordedAt,
    duration,
    title,
    keyMoment,
    reserveKeyMomentSpace,
    isExpanded,
    isStuck,
    showKeyMomentRail,
    chaptersId,
    onPress,
    className,
  } = props;

  return (
    <NavigationRailHeaderItem
      data-call-header
      hasRail={showKeyMomentRail}
      className={twMerge(
        "relative",
        isStuck &&
          "rounded-t-none after:pointer-events-none after:absolute after:right-0 after:-bottom-3 after:left-9 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']",
        className,
      )}
      icon={<IconPhone className="size-3.5" />}
      title={title}
      metadata={
        <>
          Call {callNumber} &middot;{" "}
          {DateTime.fromISO(recordedAt).toLocaleString(DateTime.DATETIME_MED)}
          {" · "}
          <span>{renderCallLength(duration)}</span>
        </>
      }
      isExpanded={isExpanded}
      aria-controls={isExpanded ? chaptersId : undefined}
      onPress={onPress}
    >
      {keyMoment || reserveKeyMomentSpace ? (
        <span className="mt-1 flex min-h-4 items-start gap-2 text-xs font-normal text-muted-foreground">
          {keyMoment ? (
            <>
              <span className="shrink-0 tabular-nums">
                {Duration.fromMillis(keyMoment.start_timestamp).toFormat(
                  "h:mm:ss",
                )}
              </span>
              <span>{keyMoment.title}</span>
            </>
          ) : null}
        </span>
      ) : null}
    </NavigationRailHeaderItem>
  );
};

export interface CallKeyMomentRowProps {
  segment: CallOutlineSegment;
  isAudioActive: boolean;
  isReadingCurrent: boolean;
  onPress: () => void;
}

export const CallKeyMomentRow: FC<CallKeyMomentRowProps> = (props) => {
  const { segment, isAudioActive, isReadingCurrent, onPress } = props;

  return (
    <NavigationRailItem
      className={twMerge(
        isReadingCurrent && "bg-gray-50",
        isAudioActive && "bg-gray-100 hover:bg-gray-100",
      )}
      aria-current={isAudioActive ? "location" : undefined}
      onPress={onPress}
    >
      <span className="min-w-0 font-medium break-words">{segment.title}</span>
      <span className="shrink-0 font-normal text-muted-foreground tabular-nums">
        {Duration.fromMillis(segment.start_timestamp).toFormat("h:mm:ss")}
      </span>
    </NavigationRailItem>
  );
};

interface CallKeyMomentListProps {
  recordingId: RecordingId;
  segments: readonly CallOutlineSegment[];
  audioPosition: CallPosition | undefined;
  readingPosition: TranscriptPosition | undefined;
  onSegmentRequest: (position: CallPosition) => void;
}

const CallKeyMomentList: FC<CallKeyMomentListProps> = (props) => {
  const {
    recordingId,
    segments,
    audioPosition,
    readingPosition,
    onSegmentRequest,
  } = props;
  const listRef = useRef<HTMLOListElement>(null);
  const { rows, railHeight } = useNavigationRailItems(listRef, segments);
  const timestamp =
    audioPosition?.recordingId === recordingId
      ? audioPosition.timestamp
      : undefined;
  const activeIndex = segments.findIndex((segment) =>
    checkCallSegmentContainsPosition(recordingId, segment, audioPosition),
  );
  const thumb =
    timestamp === undefined
      ? undefined
      : computeCallKeyMomentThumb(rows, timestamp, railHeight);

  return (
    <NavigationRail listRef={listRef} thumb={thumb}>
      {segments.map((segment, index) => (
        <li
          key={`${segment.title}-${segment.start_timestamp}-${segment.end_timestamp}`}
        >
          <CallKeyMomentRow
            segment={segment}
            isAudioActive={index === activeIndex}
            isReadingCurrent={checkCallSegmentContainsPosition(
              recordingId,
              segment,
              readingPosition,
            )}
            onPress={() =>
              onSegmentRequest({
                type: "call",
                recordingId,
                timestamp: segment.start_timestamp,
              })
            }
          />
        </li>
      ))}
    </NavigationRail>
  );
};

/**
 * The thumb beside the key moment at `timestamp`, or between the moments
 * around it when it falls in a gap.
 */
function computeCallKeyMomentThumb(
  rows: readonly NavigationRailMeasuredItem<CallOutlineSegment>[],
  timestamp: number,
  railHeight: number,
): NavigationRailThumbPosition | undefined {
  if (rows.length === 0) return undefined;

  const activeRow = rows.find(
    ({ item }) =>
      timestamp >= item.start_timestamp && timestamp < item.end_timestamp,
  );
  if (activeRow) {
    return computeNavigationRailThumb(activeRow);
  }

  const previousRow = rows.findLast(
    ({ item }) => item.end_timestamp <= timestamp,
  );
  const nextRow = rows.find(({ item }) => item.start_timestamp > timestamp);
  return computeNavigationRailGapThumb(previousRow, nextRow, railHeight);
}

interface TextConversationNavigationRowProps {
  conversation: TextConversationNavigationItem;
  isReadingCurrent?: boolean;
  isExpanded?: boolean;
  /** Keeps the line a call row uses for its key moment, so the dropdown trigger keeps its height. */
  reserveKeyMomentSpace?: boolean;
  onPress?: () => void;
}

export const TextConversationNavigationRow: FC<
  TextConversationNavigationRowProps
> = (props) => {
  const {
    conversation,
    isReadingCurrent,
    isExpanded,
    reserveKeyMomentSpace,
    onPress,
  } = props;
  const messageCount = conversation.textMessageIds.length;
  return (
    <NavigationRailHeaderItem
      className={isReadingCurrent ? "bg-gray-50" : undefined}
      icon={<IconMessage className="size-3.5" />}
      title="Text conversation"
      metadata={
        <>
          {DateTime.fromISO(conversation.startedAt).toLocaleString(
            DateTime.DATETIME_MED,
          )}
          {" · "}
          {messageCount} {messageCount === 1 ? "message" : "messages"}
        </>
      }
      isExpanded={isExpanded}
      onPress={onPress}
    >
      {reserveKeyMomentSpace && (
        <span aria-hidden="true" className="mt-1 block min-h-4" />
      )}
    </NavigationRailHeaderItem>
  );
};

/** Navigates an interaction's calls, chapters, and text conversations. */
export const TranscriptNavigator: FC<TranscriptNavigatorProps> = (props) => {
  const {
    recordings,
    textThread,
    callOutline,
    isOutlineLoading,
    isOutlineError,
    expandedRecordingId,
    expandAll = false,
    audioPosition,
    readingPosition,
    onCallRequest,
    onSegmentRequest,
    onTextConversationRequest,
  } = props;
  const items = useMemo(
    () => buildTranscriptNavigationItems(recordings, callOutline, textThread),
    [recordings, callOutline, textThread],
  );
  const navigationId = useId();
  const navRef = useRef<HTMLElement>(null);
  const [stuckCallId, setStuckCallId] = useState<RecordingId>();

  useEffect(() => {
    const activeCall = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        "[data-call-recording-id]",
      ) ?? [],
    ).find(
      (element) => element.dataset.callRecordingId === expandedRecordingId,
    );
    activeCall?.scrollIntoView({ block: "nearest" });
  }, [expandedRecordingId]);

  useEffect(() => {
    const nav = navRef.current;
    const scrollPane = nav?.closest<HTMLElement>(
      "[data-call-scroll-container]",
    );
    if (!nav || !scrollPane) {
      return;
    }

    const updateStuckCall = () => {
      const stickyTop =
        scrollPane.getBoundingClientRect().top +
        Number.parseFloat(getComputedStyle(scrollPane).paddingTop);
      const stuckSection = Array.from(
        nav.querySelectorAll<HTMLElement>("[data-call-recording-id]"),
      ).find((section) => {
        const bounds = section.getBoundingClientRect();
        const stickyHeaderHeight =
          section.querySelector<HTMLElement>("[data-call-sticky-header]")
            ?.offsetHeight ?? 0;
        return (
          bounds.top < stickyTop - 1 &&
          bounds.bottom > stickyTop + stickyHeaderHeight
        );
      });
      const nextId = recordings.find(
        (recording) => recording.id === stuckSection?.dataset.callRecordingId,
      )?.id;
      setStuckCallId((currentId) =>
        currentId === nextId ? currentId : nextId,
      );
    };

    scrollPane.addEventListener("scroll", updateStuckCall, { passive: true });
    window.addEventListener("resize", updateStuckCall);
    updateStuckCall();
    return () => {
      scrollPane.removeEventListener("scroll", updateStuckCall);
      window.removeEventListener("resize", updateStuckCall);
    };
  }, [expandAll, expandedRecordingId, items, recordings]);

  return (
    <nav ref={navRef} aria-label={describeTranscriptNavigationItems(items)}>
      <ol>
        {items.map((item) => {
          if (item.type === "textConversation") {
            const textConversationId = item.id;
            return (
              <li key={textConversationId} className="pt-2">
                <TextConversationNavigationRow
                  conversation={item}
                  isReadingCurrent={
                    readingPosition?.type === "textMessage" &&
                    item.textMessageIds.includes(readingPosition.textMessageId)
                  }
                  onPress={() => onTextConversationRequest(textConversationId)}
                />
              </li>
            );
          }
          const { id, startedAt, duration, callNumber, title, segments } = item;
          const isExpanded = expandAll || id === expandedRecordingId;
          const chaptersId = `${navigationId}-call-${id}-chapters`;

          return (
            <li key={id} data-call-recording-id={id}>
              <div
                data-call-sticky-header
                className="sticky top-0 z-10 before:block before:h-2 before:bg-white before:content-['']"
              >
                <CallNavigationRow
                  callNumber={callNumber}
                  recordedAt={startedAt}
                  duration={duration}
                  title={title}
                  isExpanded={expandAll ? undefined : isExpanded}
                  isStuck={stuckCallId === id}
                  showKeyMomentRail={isExpanded && segments.length > 0}
                  chaptersId={chaptersId}
                  onPress={() => {
                    if (expandAll || !isExpanded) {
                      onCallRequest(id);
                    }
                  }}
                />
              </div>

              {isExpanded ? (
                <div id={chaptersId}>
                  {segments.length > 0 ? (
                    <CallKeyMomentList
                      recordingId={id}
                      segments={segments}
                      audioPosition={audioPosition}
                      readingPosition={readingPosition}
                      onSegmentRequest={onSegmentRequest}
                    />
                  ) : isOutlineLoading ? (
                    <div className="px-3 py-2">
                      <DefaultSkeleton />
                    </div>
                  ) : isOutlineError ? (
                    <p className="px-3 py-2 text-sm text-destructive">
                      Failed to load call chapters.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
